import { OwnersRepository } from '~/repositories/ownersRepository';
import { PayoutMethod } from '~/swagger/portal/Api';
import type {
  DisburseOwnerDistributionLineDto,
  DisburseOwnerDistributionsDto,
  DisburseOwnerDistributionsGroupDto,
} from '~/swagger/portal/Api';
import type { OwnerDistributionEditingGroup, OwnerDistributionsStoreData } from '~/types/ownerDistributions';

const ownersRepo = new OwnersRepository();

function buildDisburseOwnerDistributionsPayload(storeData: OwnerDistributionsStoreData): DisburseOwnerDistributionsDto {
  const filtered = storeData.editingDistributions.filter((d) => {
    if (d.distributionAmount <= 0 || !d.paymentBankAccountId) return false;
    if (d.paymentType === PayoutMethod.Ach && !d.receivingBankAccountId) return false;
    if (d.paymentType === PayoutMethod.Manual && !d.externalTransactionId) return false;
    return true;
  });

  const findCommonData = (group: OwnerDistributionEditingGroup) => {
    const first = filtered.find((d) => d.ownerId === group.ownerId && group.propertyIds.includes(d.propertyId));
    if (!first) return undefined;
    return {
      bankAccountId: first.paymentBankAccountId,
      paymentMethodId: first.receivingBankAccountId,
      paymentType: first.paymentType,
      paymentMethod: first.paymentMethod,
      externalTransactionId: first.externalTransactionId,
      memo: first.commonMemo,
      distributionDate: first.distributionDate,
    };
  };

  const groups: DisburseOwnerDistributionsGroupDto[] = [];

  for (const group of storeData.editingDistributionGroups) {
    const commonData = findCommonData(group);
    const lines: DisburseOwnerDistributionLineDto[] = [];
    for (const propertyId of group.propertyIds) {
      const data = filtered.find((d) => d.propertyId === propertyId && d.ownerId === group.ownerId);
      if (data) {
        lines.push({
          propertyId,
          amount: data.distributionAmount,
          memo: data.memo,
        });
      }
    }

    if (commonData && lines.length) {
      const bankAccount = storeData.bankAccounts.find((a) => a.id === commonData.bankAccountId);
      const cashChartOfAccountId = bankAccount?.chartOfAccountIds?.[0] ?? '';
      groups.push({
        ownerId: group.ownerId,
        bankAccountId: commonData.bankAccountId,
        cashChartOfAccountId,
        paymentType: commonData.paymentType,
        paymentMethod: commonData.paymentMethod,
        externalTransactionId: commonData.externalTransactionId,
        paymentMethodId: commonData.paymentMethodId,
        memo: commonData.memo,
        distributionDate: commonData.distributionDate,
        lines,
      });
    }
  }

  return { groups };
}

export function useOwnerDistributionsPay() {
  const submitDistributions = async (storeData: OwnerDistributionsStoreData) => {
    const payload = buildDisburseOwnerDistributionsPayload(storeData);
    return ownersRepo.disburseOwnerDistributions(payload);
  };

  return { submitDistributions };
}
